/**
 * ABDP-style table pagination: prev/next chevrons, numbered pages, page-size
 * dropdown ("Table List"), and total page count ("of N"). Parent owns state.
 */
const PAGE_SIZES = [10, 20, 50];

function pageWindow(page, totalPages) {
  if (totalPages <= 3) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const pages = [];
  const start = Math.max(1, Math.min(page - 1, totalPages - 2));
  const end = Math.min(totalPages, start + 2);
  for (let p = start; p <= end; p += 1) pages.push(p);
  return pages;
}

function ChevronLeft({ className = '' }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
    >
      <path
        d="M7.5 9.5L4 6l3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRight({ className = '' }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
    >
      <path
        d="M4.5 2.5L8 6l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = PAGE_SIZES,
  disabled = false,
  listLabel = 'Table List',
  showItemRange = false,
  className = '',
  'aria-label': ariaLabel = 'Table pagination',
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const pages = pageWindow(page, totalPages);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total || 0, page * pageSize);
  const hideNav = totalPages <= 1 && !onPageSizeChange;

  const squareBtn =
    'inline-flex h-8 min-w-8 items-center justify-center rounded border px-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40';
  const navBtn = `${squareBtn} border-[#003B5B]/35 bg-white text-[#003B5B]/80 hover:bg-[#F8FAFC]`;
  const pageBtnActive = `${squareBtn} border-[#003B5B] bg-[#003B5B] text-white`;
  const pageBtnIdle = `${squareBtn} border-[#003B5B]/35 bg-white text-[#1F2933] hover:bg-[#F8FAFC]`;

  if (hideNav && !showItemRange) {
    return null;
  }

  return (
    <nav
      aria-label={ariaLabel}
      className={`flex flex-wrap items-center justify-end gap-4 text-xs text-[#5A5A5A] ${className}`}
    >
      {showItemRange ? (
        <span className="mr-auto text-xs text-[#5A5A5A]">
          {from}–{to} of {total || 0}
        </span>
      ) : null}

      {totalPages > 1 ? (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={navBtn}
            aria-label="Previous page"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={disabled || page <= 1}
          >
            <ChevronLeft />
          </button>
          {pages[0] > 1 ? (
            <span className="px-0.5 text-[#5A5A5A]" aria-hidden>
              …
            </span>
          ) : null}
          {pages.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              disabled={disabled}
              aria-current={p === page ? 'page' : undefined}
              aria-label={p === page ? `Page ${p}, current` : `Page ${p}`}
              className={p === page ? pageBtnActive : pageBtnIdle}
            >
              {p}
            </button>
          ))}
          {pages[pages.length - 1] < totalPages ? (
            <span className="px-0.5 text-[#5A5A5A]" aria-hidden>
              …
            </span>
          ) : null}
          <button
            type="button"
            className={navBtn}
            aria-label="Next page"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={disabled || page >= totalPages}
          >
            <ChevronRight />
          </button>
        </div>
      ) : null}

      {onPageSizeChange ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#1F2933]">{listLabel}</span>
          <select
            value={pageSize}
            disabled={disabled}
            aria-label={`${listLabel} rows per page`}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 min-w-[3.25rem] rounded border border-[#D8D4D4] bg-white px-2 py-1 text-xs font-semibold text-[#1F2933] focus:border-[#003B5B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003B5B]/25"
          >
            {pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-xs text-[#1F2933]">of {totalPages}</span>
        </div>
      ) : totalPages > 1 ? (
        <span className="text-xs text-[#1F2933]">of {totalPages}</span>
      ) : null}
    </nav>
  );
}
